import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

function ToolbarButton({ active, onClick, title, children }) {
    return (
        <button
            type="button"
            title={title}
            className={`btn btn-sm py-0 px-2 ${active ? 'btn-secondary' : 'btn-outline-secondary'}`}
            style={{ fontSize: '0.8rem', lineHeight: '1.6' }}
            onMouseDown={(e) => {
                e.preventDefault();
                onClick();
            }}
        >
            {children}
        </button>
    );
}

export default function RichTextEditor({ value, onChange, disabled, minHeight = 220 }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: { openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } },
            }),
        ],
        content: value || '',
        editable: !disabled,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    // Sync value in when editing an existing campaign
    useEffect(() => {
        if (!editor || !value) return;
        const current = editor.getHTML();
        if (current !== value) {
            editor.commands.setContent(value, false);
        }
    }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

    const addLink = () => {
        const url = window.prompt('URL');
        if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <div className={`border rounded overflow-hidden ${disabled ? 'bg-light' : ''}`}>
            {editor && !disabled && (
                <div className="d-flex flex-wrap align-items-center gap-1 px-2 py-1 border-bottom bg-light">
                    <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
                        <strong>B</strong>
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
                        <em>I</em>
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
                        <span style={{ textDecoration: 'underline' }}>U</span>
                    </ToolbarButton>
                    <span className="text-muted px-1">|</span>
                    <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
                        H2
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
                        H3
                    </ToolbarButton>
                    <span className="text-muted px-1">|</span>
                    <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list">
                        ≡
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
                        №
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
                        "
                    </ToolbarButton>
                    <span className="text-muted px-1">|</span>
                    <ToolbarButton active={editor.isActive('link')} onClick={addLink} title="Add link">
                        Link
                    </ToolbarButton>
                    {editor.isActive('link') && (
                        <ToolbarButton active={false} onClick={() => editor.chain().focus().unsetLink().run()} title="Remove link">
                            Unlink
                        </ToolbarButton>
                    )}
                </div>
            )}
            <EditorContent
                editor={editor}
                style={{ minHeight, padding: '0.75rem', outline: 'none' }}
            />
        </div>
    );
}
